import type { OpenResource, ResourceAccessor, ResourceLocation } from './resource';
import { ResourceError } from './resource';
import type { BasicFetch, Response } from '../types/basicFetch';
import type { Vfs } from '../types/vfs';
import { readableStreamFromArray } from '../async/readableStreamFromArray';
import { readableFileStream } from './fileStream';
import { resilientFetch } from './resilientFetch';
import type { UrlProvider } from './urlProvider';
import type { Clock } from '../types/clock';

export class DefaultResourceAccessor implements ResourceAccessor {
  constructor(private services: { vfs: Vfs, fetch: BasicFetch, clock: Clock }) { }

  async open(resourceLocation: ResourceLocation, start: number, end: | null): Promise<OpenResource> {
    switch (resourceLocation.type) {
      case 'http':
        return await this.openHttp(resourceLocation.provider, start, end);
      case 'file':
        return await this.openFile(resourceLocation.filename, start, end);
      case 'memory':
        const bytes =
          resourceLocation.bytes.subarray(start, end ?? undefined);

        return {
          stream: readableStreamFromArray([bytes]),
          size: bytes.length,
        };
    }
  }

  async getSize(resourceLocation: ResourceLocation): Promise<number> {
    switch (resourceLocation.type) {
      case 'http':
        return await this.getHttpSize(resourceLocation.provider);
      case 'file':
        return await this.getFileSize(resourceLocation.filename);
      case 'memory':
        return resourceLocation.bytes.length;
    }
  }

  private async openHttp(
    urlProvider: UrlProvider,
    start: number,
    end: number | null
  ): Promise<OpenResource> {
    const { body, range } = await this.httpFetchAndCheckErrors(urlProvider, start, end);
    if ((range.start !== start) || ((end !== null) && (range.end !== end))) {
      throw new ResourceError(`Server returned wrong range. Expected: [${start}-${end}>. Got: [${range.start}, ${range.end}>`, 'EIO');
    }
    return { stream: body, size: range.end - range.start };
  }

  private async getHttpSize(urlProvider: UrlProvider): Promise<number> {
    const { range } = await this.httpFetchAndCheckErrors(urlProvider, 0, 1);
    if (range.size === null) {
      throw new ResourceError('Server did not return content size', 'EIO');
    }
    return range.size;
  }

  private async fetchUrlWithRetry(
    urlProvider: UrlProvider,
    headers: string[][],
  ) {
    const rFetch = resilientFetch(
      { fetch: this.services.fetch, AbortController },
      {},
      this.services.clock,
      null,
    );

    let nRetries = 0;
    let response: Response;
    while (true) {
      response = await rFetch(urlProvider.getUrl(), { headers });
      if (response.ok) return response;

      // Refresh URL if token used to sign the URL has expired
      const isExpiredToken = await isExpiredTokenError(response);
      if (!isExpiredToken) return response;

      await urlProvider.refreshUrl();

      nRetries += 1;

      if (nRetries >= 3) break;
    }

    return response;
  }

  private async httpFetchAndCheckErrors(
    urlProvider: UrlProvider,
    start: number,
    end: number | null
  ): Promise<ParsedHttpResponse> {
    // Ideally we run a HEAD request, but high res URLs might only be signed for GET which
    // means that in order to get the size of the highres we just have to use a range request
    const headers = [];
    if ((start !== 0) || (end !== null)) {
      let rangeHeaderValue = `bytes=${start}-`;
      if (end != null) {
        rangeHeaderValue += `${end - 1}`;
      }
      headers.push(['Range', rangeHeaderValue]);

    }
    const response = await this.fetchUrlWithRetry(urlProvider, headers);
    if (!response.ok) {
      const code = (response.status === 404) ? 'ENOENT' : 'EIO';
      const error = new ResourceError(`HTTP request failed: ${response.status} ${response.statusText}, header=${JSON.stringify(headers)}`, code);
      error.httpStatus = response.status;
      error.httpStatusText = response.statusText;
      throw error;
    }
    if (!response.body) {
      throw new ResourceError('Expected HTTP body', 'EIO');
    }

    const contentRange = response.headers.get('Content-Range');
    if (contentRange !== null) {
      const range = parseContentRange(contentRange);
      if (range === null) {
        throw new ResourceError(`Invalid Content-Range value: ${contentRange}`, 'EIO');
      }
      return { body: response.body, range };
    }
    const contentLength = response.headers.get('Content-Length');
    if (contentLength !== null) {
      const size = parseInt(contentLength);
      if (!isFinite(size)) {
        throw new ResourceError(`Invalid Content-Length value: ${contentRange}`, 'EIO');
      }

      return {
        body: response.body,
        range: {
          start: 0,
          end: size,
          size,
        }
      };
    }
    throw new ResourceError('Expected Content-Length or Content-Range', 'EIO');
  }

  private async openFile(filename: string, start: number, end: number | null): Promise<OpenResource> {
    const { vfs } = this.services;
    const endOrSize = (end === null) ? await this.getFileSize(filename) : end;
    const size = endOrSize - start;
    return {
      stream: readableFileStream(vfs, filename, start, size),
      size,
    };
  }

  private async getFileSize(filename: string): Promise<number> {
    const { vfs } = this.services;
    const stat = await vfs.stat(filename);
    return stat.size;
  }
}

interface ParsedHttpResponse {
  body: ReadableStream<Uint8Array>,
  range: ParsedContentRange,
}

interface ParsedContentRange {
  start: number,
  end: number,
  size: number | null,
}

function parseContentRange(contentRange: string): ParsedContentRange | null {
  // https://developer.mozilla.org/en-US/docs/Web/HTTP/Headers/Content-Range
  // Content-Range: <unit> <range-start>-<range-end>/<size>
  // Content-Range: <unit> <range-start>-<range-end>/*
  // Content-Range: <unit> */<size>
  // Content-Range: bytes 0-0/11790384

  const [bytesToken, spec] = contentRange.split(' ');
  if ((bytesToken !== 'bytes') || (spec === undefined)) {
    return null;
  }
  const [rangeSpec, sizeSpec] = spec.split('/');
  if ((rangeSpec === undefined) || (sizeSpec === undefined)) {
    return null;
  }

  let size = null;
  if (sizeSpec !== '*') {
    size = parseInt(sizeSpec);
    if (!isFinite(size)) {
      return null;
    }
  }

  if (rangeSpec === '*') {
    if (size === null) {
      return null;
    }
    return { start: 0, end: size, size };
  } else {
    const [start, end] = rangeSpec.split('-').map((s) => parseInt(s));
    if (!isFinite(start) || !isFinite(end)) {
      return null;
    }
    return { start, end: end + 1, size };
  }
}

async function isExpiredTokenError(response: Response): Promise<boolean> {
  if (response.status === 400) {
    const data = response.body;
    if (data === null) return false;

    const text = await response.text();
    return text.includes('ExpiredToken');
  }

  return false;
}
